import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import MustChangePasswordDialog from "./MustChangePasswordDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutGrid, Briefcase, Users, Wallet, Sparkles, LogOut,
  UserRound, Mail, UserPlus, Factory, ExternalLink, ChevronDown, Award,
} from "lucide-react";

const NAV_BY_ROLE = {
  admin: [
    { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/dashboard/jobs", label: "Jobs", icon: Briefcase },
    { to: "/dashboard/applications", label: "Applications", icon: Users },
    { to: "/dashboard/users", label: "Signed-up Users", icon: UserRound },
    { to: "/dashboard/invite", label: "Staff & Clients", icon: UserPlus },
    { to: "/dashboard/messages", label: "Messages", icon: Mail },
    { to: "/dashboard/industries", label: "Industries", icon: Factory },
    { to: "/dashboard/clients", label: "Clients", icon: Users },
    { to: "/dashboard/payroll", label: "Payroll Hub", icon: Wallet },
    { to: "/dashboard/screen", label: "AI Screening", icon: Sparkles },
  ],
  client: [
    { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/dashboard/jobs", label: "Post a Job", icon: Briefcase },
    { to: "/dashboard/payroll", label: "Payroll Hub", icon: Wallet },
  ],
  candidate: [
    { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/dashboard/applications", label: "My Applications", icon: Briefcase },
    { to: "/dashboard/rcp", label: "RCP Program", icon: Award },
    { to: "/dashboard/screen", label: "AI Resume Match", icon: Sparkles },
  ],
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV_BY_ROLE[user?.role || "candidate"];
  const isCandidate = user?.role === "candidate";

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-navy flex" data-testid="dashboard-layout">
      <aside className="w-64 shrink-0 bg-white border-r border-navy/10 flex flex-col">
        <div className="p-6 border-b border-navy/10">
          <Link to="/" className="flex items-center gap-3">
            <img src="/ravemora-logo.png" alt="Ravemora" className="w-9 h-9 object-contain" />
            <div className="serif-display text-navy text-lg font-black">Ravemora</div>
          </Link>
          <div className="label-eyebrow text-gold mt-4">{user?.role} portal</div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`side-${n.label.replace(/\s|&/g, "-").toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 border-l-2 label-eyebrow transition-colors ${isActive ? "bg-navy text-gold border-gold" : "border-transparent text-navy/70 hover:text-navy hover:bg-slateBg"}`}
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        {isCandidate && (
          <div className="p-4 border-t border-navy/10 space-y-1" data-testid="sidebar-candidate-footer">
            <a
              href="/"
              className="flex items-center gap-3 px-3 py-2.5 label-eyebrow text-navy/70 hover:text-navy hover:bg-slateBg transition-colors"
              data-testid="sidebar-public-site"
            >
              <ExternalLink size={16} /> Public site
            </a>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 label-eyebrow text-red-600 hover:bg-red-50 transition-colors"
              data-testid="sidebar-signout"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        )}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-navy/10 h-16 flex items-center justify-end px-6 md:px-10 gap-3" data-testid="dashboard-topbar">
          {user?.role === "admin" && (
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="label-eyebrow text-navy/60 hover:text-gold inline-flex items-center gap-2"
              data-testid="topbar-public-site"
            >
              <ExternalLink size={14} /> Public site
            </a>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 pl-4 py-1.5 border-l border-navy/10 hover:bg-slateBg transition-colors pr-2" data-testid="topbar-user-menu">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 bg-navy text-gold flex items-center justify-center serif-display">{user?.name?.[0] || "R"}</div>
                )}
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-semibold text-navy truncate max-w-[160px]">{user?.name}</div>
                  <div className="text-[11px] text-navy/50 uppercase tracking-wider">{user?.role}</div>
                </div>
                <ChevronDown size={14} className="text-navy/40" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="topbar-user-menu-content">
              <DropdownMenuLabel>
                <div className="serif-display text-base text-navy leading-tight">{user?.name}</div>
                <div className="text-xs text-navy/60 truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.role === "admin" && (
                <DropdownMenuItem onClick={() => window.open("/", "_blank")} data-testid="menu-public-site">
                  <ExternalLink size={14} className="mr-2" /> Open public site
                </DropdownMenuItem>
              )}
              {user?.role === "admin" && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-red-600 focus:text-red-700"
                data-testid="menu-signout"
              >
                <LogOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-8 md:p-12">
          <Outlet />
        </main>
      </div>

      <MustChangePasswordDialog />
    </div>
  );
}
